import type { User } from '@/types/directory/User';
import type { ReviewAudience, ReviewEvaluatorSelection } from '@/types/review/ReviewAudience';

export type Reviewee = User & {
    reviewers: User[];
    reviewAudienceAssignmentId: string;
};

interface GetRevieweesTransformedDataParams {
    reviewAudience?: ReviewAudience;
    reviewEvaluatorSelection?: ReviewEvaluatorSelection;
    reviewees?: User[];
    users?: User[];
}

export const getRevieweeIdsList = (
    reviewEvaluatorSelection: ReviewEvaluatorSelection,
): string[] => {
    const revieweeIds = (reviewEvaluatorSelection.reviewEvaluatorAssignments ?? [])
        .map((assignment) => assignment.reviewAudienceAssignment?.assignedTo)
        .filter((id): id is string => id !== undefined);

    return [...new Set(revieweeIds)];
};

export const getReviewerIdsList = (
    reviewEvaluatorSelection: ReviewEvaluatorSelection,
): string[] => {
    const reviewerIds = (reviewEvaluatorSelection.reviewEvaluatorAssignments ?? []).map(
        (assignment) => assignment.assignedTo,
    );

    return [...new Set(reviewerIds)];
};

export const getRevieweesTransformedData = ({
    reviewAudience,
    reviewEvaluatorSelection,
    reviewees,
    users,
}: GetRevieweesTransformedDataParams): Reviewee[] => {
    if (!reviewAudience || !reviewees) {
        return [];
    }

    const evaluatorAssignments = reviewEvaluatorSelection?.reviewEvaluatorAssignments ?? [];

    return reviewees.map((reviewee) => {
        const reviewAudienceAssignment = reviewAudience.reviewAudienceAssignments.find(
            (assignment) => assignment.assignedTo === reviewee.id,
        );
        const reviewAudienceAssignmentId = reviewAudienceAssignment?.id ?? '';

        const reviewerAssignments = evaluatorAssignments.filter(
            (assignment) => assignment.reviewAudienceAssignmentId === reviewAudienceAssignmentId,
        );

        const reviewerIds = reviewerAssignments.map((assignment) => assignment.assignedTo);
        const reviewers = users?.filter((user) => reviewerIds.includes(user.id)) ?? [];

        return {
            ...reviewee,
            reviewers,
            reviewAudienceAssignmentId,
        };
    });
};

export const getReviewees = ({
    reviewEvaluatorSelection,
    users,
}: GetRevieweesTransformedDataParams): User[] => {
    if (!reviewEvaluatorSelection || !users) {
        return [];
    }

    const revieweeIds = getRevieweeIdsList(reviewEvaluatorSelection);

    return users.filter((user) => revieweeIds.includes(user.id));
};
